// Подробности работы корзины https://github.yandex-team.ru/Billing/balance-cart

import * as React from 'react';
import { RefObject } from 'react';

import { Bem } from '../../service/Bem/Bem';
import { UrlHelpers } from '../../service/UrlHelpers';
import { LinkTarget } from '../../typings/common';

import './BalanceCart.css';

const BLOCK = 'balance-cart';
const BALANCE_CART_VERSION = 1;
const BALANCE_DIRECT_SERVICE_ID = '7'; // id Директа

interface YandexBalanceMessageDataListItem {
    qty: string;
    service_id: string;
    service_order_id: string;
}

interface YandexBalanceMessageListData {
    method: BalanceCartMethods.LIST;
    items: YandexBalanceMessageDataListItem[];
    single_account_number: string | null;
}

interface YandexBalanceMessageAddData {
    method: BalanceCartMethods.ADD;
    qty: string;
}

interface YandexBalanceMessageClickData {
    method: BalanceCartMethods.CLICK;
}

interface YandexBalanceMessageError {
    status: string;
    message: string;
    description: string;
    method: BalanceCartMethods;
}

type YandexBalanceMessageData =
    | YandexBalanceMessageListData
    | YandexBalanceMessageAddData
    | YandexBalanceMessageClickData;

interface YandexBalanceMessage {
    error?: YandexBalanceMessageError;
    data?: YandexBalanceMessageData;
    version: string;
}

export enum BalanceCartMethods {
    LIST = 'list',
    ADD = 'add',

    // https://st.yandex-team.ru/BALANCE-32175
    CLICK = 'click'
}

interface AddItemData {
    qty: string; // добавляемая сумма в виде строки
    service_id: typeof BALANCE_DIRECT_SERVICE_ID; // service_id = 7 для Директа
    service_order_id: string; // под service_order_id понимается такой cid кампании, тип которой = wallet (такой cid один на клиента)
    old_qty?: string; // предыдущее значение добавленной суммы в корзину
}

type AddAmount = (amount: string, oldAmount: string, serviceOrderId: string) => void;

interface BalanceCartProps {
    host: string;
    onFirstDataLoaded?: (hasSinglePersonalAccount: boolean, serviceItems: YandexBalanceMessageDataListItem[]) => void;
    onAddAmount?: (amount: string) => void;
    onError?: (error: YandexBalanceMessageError) => void;
    onComponentInited?: (addAmount: AddAmount) => void;
}

interface BalanceCartState {
    isComponentVisible: boolean;
    isCartVisible: boolean;
}

type YandexBalanceHandler = (data: YandexBalanceMessage) => void;

// Компонент используется в DNA и большом Директе
class BalanceCart extends React.PureComponent<BalanceCartProps, BalanceCartState> {
    state = {
        isComponentVisible: true,
        isCartVisible: false
    };

    private iframeRef: RefObject<HTMLIFrameElement> = React.createRef();

    componentDidMount(): void {
        if (!this.iframeRef.current) {
            throw new Error('iframe not found');
        }

        window.addEventListener('message', this.onIframeMessage);
        this.iframeRef.current.src = this.getBalaceCartIframeUrl();

        this.onComponentInited();
    }

    componentWillUnmount(): void {
        window.removeEventListener('message', this.onIframeMessage);
    }

    render() {
        const { isComponentVisible, isCartVisible } = this.state;

        return (
            isComponentVisible && (
                <iframe
                    ref={this.iframeRef}
                    className={Bem.cls(BLOCK, undefined, {
                        visible: isCartVisible
                    })}
                    sandbox={'allow-same-origin allow-scripts allow-top-navigation-by-user-activation'}
                />
            )
        );
    }

    private getBalaceCartIframeUrl = () => {
        return `${this.props.host}/cart-frame.html?service_id=${BALANCE_DIRECT_SERVICE_ID}`;
    };

    private getBalanceUrl = () => {
        return `${this.props.host}/cart.xml?service_id=${BALANCE_DIRECT_SERVICE_ID}`;
    };

    private onIframeMessage = (event: MessageEvent): void => {
        if (event.origin !== this.props.host) {
            return;
        }

        this.balanceCartHandler(event.data);
    };

    private balanceCartHandler: YandexBalanceHandler = (messageData: YandexBalanceMessage) => {
        if (messageData.error) {
            this.onError(messageData.error);
            return;
        }

        if (!messageData.data) {
            throw new Error('messageData is empty');
        }

        switch (messageData.data.method) {
            case BalanceCartMethods.LIST:
                this.onFirstDataLoaded(messageData.data);
                break;
            case BalanceCartMethods.ADD:
                this.onAddAmount(messageData.data);
                break;
            case BalanceCartMethods.CLICK:
                this.onClickByBalanceCart();
                break;
            default:
                throw new Error('Unhandled method');
        }
    };

    private onComponentInited = () => {
        if (this.props.onComponentInited) {
            this.props.onComponentInited(this.triggerAddAmount);
        }
    };

    private triggerAddAmount = (amount: string, oldAmount: string, serviceOrderId: string) => {
        const data: AddItemData = {
            qty: String(amount),
            service_id: BALANCE_DIRECT_SERVICE_ID,
            service_order_id: String(serviceOrderId)
        };

        if (oldAmount) {
            data.old_qty = String(oldAmount);
        }

        if (!this.iframeRef.current) {
            throw new Error('iframe not found');
        }

        if (!this.iframeRef.current.contentWindow) {
            throw new Error('contentWindow not found');
        }

        this.iframeRef.current.contentWindow.postMessage(
            {
                data: data,
                version: BALANCE_CART_VERSION
            },
            this.props.host
        );
    };

    private onFirstDataLoaded = (messageListData: YandexBalanceMessageListData) => {
        const hasSinglePersonalAccount = Boolean(messageListData.single_account_number);

        // Запрещено отображать корзину без "Единого лицевого счета" (ЕЛС)
        if (!hasSinglePersonalAccount) {
            this.setState({ isComponentVisible: false });

            // ЕЛС есть, нужно фрейму установить размеры
        } else {
            this.setState({ isCartVisible: hasSinglePersonalAccount });
        }

        if (this.props.onFirstDataLoaded) {
            this.props.onFirstDataLoaded(hasSinglePersonalAccount, messageListData.items);
        }
    };

    private onAddAmount = (messageAddData: YandexBalanceMessageAddData) => {
        if (this.props.onAddAmount) {
            this.props.onAddAmount(messageAddData.qty);
        }
    };

    private onClickByBalanceCart = () => {
        const url = this.getBalanceUrl();

        UrlHelpers.openUrl(url, LinkTarget.blank);
    };

    private onError = (error: YandexBalanceMessageError) => {
        if (this.props.onError) {
            this.props.onError(error);
        }

        // Произошла ошибка при загрузке данных самой корзины
        if (error.method === BalanceCartMethods.LIST) {
            window.removeEventListener('message', this.onIframeMessage);
            throw new Error('dna balance cart error');
        }
    };
}

export { BalanceCart };
